import { IonItem, IonPage, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon, IonContent, IonLabel, IonList, IonText, IonSpinner, IonAvatar, useIonAlert } from "@ionic/react";
import { close, shareOutline } from 'ionicons/icons';
import { useDeleteDeviceUserAccessMutation, useDeviceUsersQuery } from "../query/deviceUsersQuery";
import { useDeviceQuery } from "../query/deviceQuery";
import { useCurrentUserQuery } from "../query/currentUserQuery";
import useUX from "../utils/ux";
import { Share } from "@capacitor/share";
import { useCreateDeviceUserInviteMutation } from "../query/deviceUserInviteQuery";
import { useTranslation } from "../hooks/useTranslation";
import ModalFooter from "../components/ModalFooter";
import footerStyles from '../components/ModalFooter.module.css';
import styles from './DeviceUsersModal.module.css';

function DeviceUsersModal({ dismiss, deviceId }: { dismiss: (data?: string | null | undefined | number, role?: string) => void, deviceId: string }) {
    const { data: users, isLoading, isError } = useDeviceUsersQuery(deviceId);
    const deviceQuery = useDeviceQuery(deviceId);
    const currentUserQuery = useCurrentUserQuery();
    const deleteDeviceUserAccessMutation = useDeleteDeviceUserAccessMutation(deviceId);
    const { presentResult } = useUX();
    const createDeviceUserInviteMutation = useCreateDeviceUserInviteMutation(deviceId);
    const { t } = useTranslation('modals');
    const { t: tCommon } = useTranslation('common');
    const [presentAlert] = useIonAlert();

    const deviceName = deviceQuery.data?.description || deviceId;

    const handleShareDevice = () => {
        presentResult(async () => {
            const result = await createDeviceUserInviteMutation.mutateAsync();
            const inviteLink = `https://onlycat.app/account/invite/${result.inviteToken}`;

            Share.share({
                title: t('device_users.share.title'),
                text: t('device_users.share.text'),
                url: inviteLink,
                dialogTitle: t('device_users.share.dialog_title')
            }).catch(error => console.log(error));
        }, t('device_users.invite_link_created'));
    };

    const confirmRemoveUser = (userId: number, displayName: string) => {
        presentAlert({
            header: t('device_users.remove_access.header'),
            message: t('device_users.remove_access.message', { name: displayName, deviceName }),
            buttons: [
                {
                    text: tCommon('buttons.cancel'),
                    role: 'cancel'
                },
                {
                    text: t('device_users.remove_access.remove'),
                    role: 'destructive',
                    handler: () => {
                        presentResult(
                            () => deleteDeviceUserAccessMutation.mutateAsync(userId),
                            t('device_users.remove_access.success')
                        );
                    }
                }
            ]
        });
    };

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonTitle>{t('device_users.title')}</IonTitle>
                    <IonButtons slot="primary">
                        <IonButton onClick={() => dismiss(null, 'cancel')} aria-label={t('device_users.close')}>
                            <IonIcon slot="icon-only" icon={close} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent color="light">
                <div className={styles.wrapper}>
                    <div className={styles.deviceCard}>
                        <img className={styles.deviceImage} src="/assets/device/onlycat-device.png" alt="" />
                        <div className={styles.deviceInfo}>
                            <h2 className={styles.deviceName}>{deviceName}</h2>
                            {deviceQuery.data?.description && <p className={styles.deviceId}>{deviceId}</p>}
                        </div>
                    </div>

                    <section className={styles.section}>
                        <h3 className={styles.sectionLabel}>
                            {t('device_users.people_with_access')}
                            {users && <span className={styles.sectionCount}>{users.length}</span>}
                        </h3>
                        <IonList className={styles.listCard}>
                            {isLoading && (
                                <div className={styles.spinnerContainer}>
                                    <IonSpinner />
                                </div>
                            )}

                            {isError && (
                                <IonItem>
                                    <IonText color="danger">{t('device_users.error_loading')}</IonText>
                                </IonItem>
                            )}

                            {users && users.length === 0 && (
                                <IonItem>
                                    <IonText>{t('device_users.no_users')}</IonText>
                                </IonItem>
                            )}

                            {users && users.map((user) => {
                                const displayName = user.name || user.email || t('device_users.default_user_name', { userId: user.id });
                                const isCurrentUser = user.id === currentUserQuery.data?.id;

                                return (
                                    <IonItem key={user.id}>
                                        <IonAvatar slot="start" className={styles.userAvatar}>
                                            <img src={user.avatarUrl || '/assets/account/default-avatar.svg'}
                                                alt={displayName}
                                                referrerPolicy="no-referrer" />
                                        </IonAvatar>
                                        <IonLabel>
                                            <h2 className={styles.userName}>
                                                <span className={styles.userNameText}>{displayName}</span>
                                                {isCurrentUser && <span className={styles.youPill}>{t('device_users.you_pill')}</span>}
                                            </h2>
                                            {user.name && user.email && <p>{user.email}</p>}
                                        </IonLabel>
                                        {!isCurrentUser && (
                                            <IonButton
                                                slot="end"
                                                fill="clear"
                                                className={styles.removeButton}
                                                disabled={deleteDeviceUserAccessMutation.isPending}
                                                onClick={() => confirmRemoveUser(user.id, displayName)}
                                                aria-label={t('device_users.remove_access.aria_label', { name: displayName })}
                                            >
                                                <IonIcon slot="icon-only" icon={close} />
                                            </IonButton>
                                        )}
                                    </IonItem>
                                );
                            })}
                        </IonList>
                        <p className={styles.caption}>{t('device_users.list_caption')}</p>
                    </section>
                </div>
            </IonContent>
            <ModalFooter>
                <IonButton
                    expand="block"
                    className={footerStyles.primaryButton}
                    onClick={handleShareDevice}
                    disabled={createDeviceUserInviteMutation.isPending}
                >
                    <IonIcon slot="start" icon={shareOutline} />
                    {t('device_users.invite_someone')}
                </IonButton>
            </ModalFooter>
        </IonPage>
    );
}

export default DeviceUsersModal;
